import { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonKPIRow } from '../../components/ui/Skeleton';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useUrlState } from '../../hooks/useUrlState';
import { useDocActions } from './useDocActions';
import { MoneyPipeline } from './MoneyPipeline';
import { DocumentsSection, NewDocumentButton, RowTypeBadge } from './DocumentsSection';
import { fmtEur, fmtDate } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';
import {
  computeUpcoming, toUnifiedRows, invoicesQueryOptions, quotesQueryOptions,
  type QuoteItem, type UnifiedRow,
} from '../../lib/scadenziario';
import './invoices.css';

/**
 * Cockpit Contabilità — pagina STORICA, fuori dal menu.
 *
 * I soldi vivono tutti in Amministrazione (/admin): la lista documenti è la
 * tab "Documenti" di quella pagina, incassi e scadenze stanno in Panoramica e
 * Incassi (dati Qonto + FiC, molto più completi di questi KPI). Questa route
 * resta viva perché i vecchi link la puntano ancora, e perché il fiume del
 * denaro (MoneyPipeline) non ha ancora una casa nuova.
 *
 * ⚠️ I KPI e lo scadenziario qui sopra leggono UNA pagina da 200 documenti
 * (invoicesQueryOptions): con 594 fatture in DB i numeri sono parziali. La
 * tab Documenti dell'Amministrazione pagina invece sul server.
 *
 * Architettura UI:
 *   page-tabs        → Flusso / Scadenziario (badge = eventi attesi) / Documenti
 *   Flusso           → MoneyPipeline (5 stadi-lente) + sezione Documenti
 *   Documenti        → DocumentsSection (la stessa dell'Amministrazione)
 *
 * Stato in URL (deep-link / bookmark): tab · type · status · q · band
 */

interface FicStatus { connected: boolean; companyName: string | null; }

// Default dei param URL: quando il valore coincide, il param viene rimosso (URL puliti).
const URL_DEFAULTS: Record<string, string> = { tab: 'scadenziario', type: 'all', status: '', q: '' };

export function InvoicesPage() {
  // Stato vista → URL (bookmarkabile; tornando dal dettaglio i filtri restano)
  const [tabParam, setTab] = useUrlState<'flusso' | 'documenti' | 'scadenziario'>('tab', 'scadenziario');
  // fallback difensivo: un ?tab sconosciuto torna al default Scadenziario
  const tab = tabParam === 'documenti' ? 'documenti' : tabParam === 'flusso' ? 'flusso' : 'scadenziario';
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Aggiornamento atomico di più param (un solo history entry, niente race
  // tra setter useUrlState consecutivi nello stesso handler).
  const setParamsBatch = (next: Partial<Record<'tab' | 'type' | 'status' | 'q', string>>) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v == null || v === URL_DEFAULTS[k]) sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };

  const { data: invoiceData, isLoading: loadingInv } = useQuery(invoicesQueryOptions);
  const { data: quoteData, isLoading: loadingQ } = useQuery(quotesQueryOptions);
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'),
  });

  const isLoading = loadingInv || loadingQ;
  const invoices = invoiceData?.items ?? [];
  const quotes = quoteData?.items ?? [];

  const rows: UnifiedRow[] = useMemo(() => toUnifiedRows(quotes, invoices), [quotes, invoices]);

  // KPI globali
  const kpis = useMemo(() => ({
    pipeline:       quotes.filter((q) => ['INVIATO', 'VISUALIZZATO'].includes(q.status)).reduce((a, q) => a + Number(q.total), 0),
    pipelineCount:  quotes.filter((q) => ['INVIATO', 'VISUALIZZATO'].includes(q.status)).length,
    toCollect:      invoices.filter((i) => ['EMESSA', 'INVIATA'].includes(i.status)).reduce((a, i) => a + Number(i.total), 0),
    toCollectCount: invoices.filter((i) => ['EMESSA', 'INVIATA'].includes(i.status)).length,
    overdue:        invoices.filter((i) => i.status === 'SCADUTA').reduce((a, i) => a + Number(i.total), 0),
    overdueCount:   invoices.filter((i) => i.status === 'SCADUTA').length,
    // "Incassato YTD": solo le fatture effettivamente PAGATA (denaro entrato).
    ytd:            invoices.filter((i) => i.status === 'PAGATA').reduce((a, i) => a + Number(i.total), 0),
  }), [quotes, invoices]);

  // Scadenziario calcolato a livello pagina: il badge del page-tab e il
  // contenuto del tab usano la STESSA lista (il numero promesso combacia).
  const upcoming = useMemo(() => computeUpcoming(rows), [rows]);

  // Metrica cliccabile = filtro (pattern P4): un click setta tab+type+status in batch.
  // 'toCollect'/'overdue' puntano allo Scadenziario (to-do azionabile: Incassa/Sollecita)
  // invece della tabella Documenti; type/status restano ai default (param rimossi dall'URL).
  const kpiFilters = {
    pipeline:  { tab: 'documenti',    type: 'quote', status: 'INVIATO' },
    toCollect: { tab: 'scadenziario', type: 'all',   status: '' },
    overdue:   { tab: 'scadenziario', type: 'all',   status: '' },
  } as const;

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora che si
            disegna, numeri NUDI sul foglio (stat-band), zero scatole ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Contabilità</h1>
          <div className="ed-sub">
            Preventivi e fatture in un unico cockpit.
            {' '}
            <a className="underline cursor-pointer" onClick={() => navigate(adminLinks.documenti())}>Amministrazione →</a>
            {' '}
            {ficStatus?.connected
              ? <Chip tone="ok" dot><Icon name="link" size={10} /> FattureInCloud · {ficStatus.companyName}</Chip>
              : <span className="text-warn">FattureInCloud non connesso — <a className="underline cursor-pointer" onClick={() => navigate('/settings')}>collega</a> per emissione fiscale.</span>}
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          <DesktopOnly
            cosa="La creazione e l'esportazione dei documenti"
            perche="fatture e preventivi sono procedure a più passaggi; dal telefono puoi consultarli e registrare gli incassi."
          >
            <>
              <NewDocumentButton />
              <ActionsMenu
                variant="ghost"
                label="Azioni"
                items={[
                  { key: 'export', label: 'Export', icon: 'export', onClick: () => useModalStore.getState().open('export', { what: 'Documenti' }) },
                ]}
              />
            </>
          </DesktopOnly>
        </div>
      </div>

      {isLoading && !invoiceData && !quoteData ? (
        <SkeletonKPIRow count={4} />
      ) : (
        <div className="stat-band">
          <div className="st-click" role="button" tabIndex={0} onClick={() => setParamsBatch(kpiFilters.pipeline)} onKeyDown={(e) => { if (e.key === 'Enter') setParamsBatch(kpiFilters.pipeline); }}>
            <div className="st-label">Pipeline preventivi</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={kpis.pipeline} format={fmtEur} /></div>
            <div className="st-delta">{kpis.pipelineCount} in attesa</div>
          </div>
          <div className="st-click" role="button" tabIndex={0} onClick={() => setParamsBatch(kpiFilters.toCollect)} onKeyDown={(e) => { if (e.key === 'Enter') setParamsBatch(kpiFilters.toCollect); }}>
            <div className="st-label">Da incassare</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={kpis.toCollect} format={fmtEur} /></div>
            <div className="st-delta">{kpis.toCollectCount} fatture aperte</div>
          </div>
          <div className="st-click" role="button" tabIndex={0} onClick={() => setParamsBatch(kpiFilters.overdue)} onKeyDown={(e) => { if (e.key === 'Enter') setParamsBatch(kpiFilters.overdue); }}>
            <div className="st-label">Scadute</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={kpis.overdue} format={fmtEur} /></div>
            {/* Rosso = solo eccezione vera: c'è almeno una fattura scaduta */}
            <div className={`st-delta${kpis.overdueCount > 0 ? ' text-alert' : ''}`}>{kpis.overdueCount} fatture</div>
          </div>
          <div>
            <div className="st-label">Incassato YTD</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={kpis.ytd} format={fmtEur} /></div>
          </div>
        </div>
      )}

      <div className="page-tabs">
        <div className="page-tab" aria-selected={tab === 'flusso'}       onClick={() => setTab('flusso')}>
          Flusso
        </div>
        <div className="page-tab" aria-selected={tab === 'scadenziario'} onClick={() => setTab('scadenziario')}>
          Scadenziario<span className="c">{upcoming.length}</span>
        </div>
        <div className="page-tab" aria-selected={tab === 'documenti'}    onClick={() => setTab('documenti')}>
          Documenti
        </div>
      </div>

      {/* Flusso: il fiume del denaro (5 stadi-lente) sopra la stessa tabella documenti */}
      {tab === 'flusso' && (
        <>
          <MoneyPipeline rows={rows} />
          <DocumentsSection />
        </>
      )}

      {tab === 'documenti' && <DocumentsSection />}

      {tab === 'scadenziario' && <ScadenziarioTab upcoming={upcoming} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Scadenziario unificato
// ════════════════════════════════════════════════════════════════════

// Scaglioni temporali: chip-filtro nel card-hd ↔ gruppi della lista (stesse chiavi)
const BAND_DEFS = [
  { key: 'overdue', label: 'Scadute' },
  { key: 'week',    label: 'Questa settimana' },
  { key: 'later',   label: 'Prossimi 60 giorni' },
] as const;
type Band = 'all' | (typeof BAND_DEFS)[number]['key'];

function ScadenziarioTab({ upcoming }: { upcoming: UnifiedRow[] }) {
  const navigate = useNavigate();
  const { markInvoicePaid, resendQuote, sendInvoiceReminder } = useDocActions();

  // Filtro scaglione → URL (come tab/type/status: bookmarkabile, back-friendly)
  const [bandParam, setBand] = useUrlState<Band>('band', 'all');
  // fallback difensivo: un ?band sconosciuto torna a 'all'
  const band: Band = BAND_DEFS.some((b) => b.key === bandParam) ? bandParam : 'all';

  const total = upcoming.reduce((a, r) => a + Number(r.total), 0);

  // Raggruppa per scaglione temporale: Scadute / Questa settimana / Più avanti.
  // SEMPRE tutti e 3 i gruppi: i count dei chip si calcolano PRIMA del filtro
  // che nasconde i gruppi vuoti (il numero promesso resta vero).
  const allGroups = useMemo(() => {
    const now = Date.now();
    const weekEnd = now + 7 * 86400000;
    const g: Record<(typeof BAND_DEFS)[number]['key'], UnifiedRow[]> = { overdue: [], week: [], later: [] };
    for (const r of upcoming) {
      const d = r.secondaryDate ? new Date(r.secondaryDate).getTime() : Infinity;
      if (d < now) g.overdue.push(r);
      else if (d <= weekEnd) g.week.push(r);
      else g.later.push(r);
    }
    return BAND_DEFS.map((b) => ({ key: b.key, label: b.label, items: g[b.key] }));
  }, [upcoming]);

  // Gruppi renderizzati: banda selezionata (o tutte) − gruppi vuoti
  const groups = allGroups.filter((s) => s.items.length > 0 && (band === 'all' || s.key === band));

  // Fallback: se la banda attiva (es. da deep-link o dopo un incasso) resta
  // senza eventi → torna a 'all'. Guard su upcoming vuoto per non azzerare
  // un deep-link prima che i dati arrivino.
  useEffect(() => {
    if (!upcoming.length) return;
    if (band !== 'all' && (allGroups.find((s) => s.key === band)?.items.length ?? 0) === 0) setBand('all');
  }, [upcoming.length, band, allGroups, setBand]);

  const renderRow = (r: UnifiedRow) => {
    const overdue = r.secondaryDate ? new Date(r.secondaryDate).getTime() < Date.now() : false;
    const days = r.secondaryDate ? Math.ceil((new Date(r.secondaryDate).getTime() - Date.now()) / 86400000) : 0;
    const urgentSoon = !overdue && days >= 0 && days < 7;
    const detailPath = r.kind === 'quote' ? `/quotes/${r.id}` : `/invoices/${r.id}`;
    // Rail laterale = eccezione vera: solo per scaduti (rosso semantico). Lo spacer transparent resta per non far saltare le righe.
    const accent = overdue ? 'var(--alert)' : 'transparent';
    return (
      <div
        key={`${r.kind}-${r.id}`}
        className="scad-row grid items-center gap-2.5 py-2.5 border-b border-line last:border-b-0 cursor-pointer"
        style={{ gridTemplateColumns: '3px 40px 90px 1fr 110px 130px 190px', borderLeft: `3px solid ${accent}` }}
        onClick={() => navigate(detailPath)}
      >
        <span aria-hidden="true" />
        <RowTypeBadge kind={r.kind} invoiceType={r.invoiceType} />
        <div className={`mono text-[12px] ${overdue ? 'text-alert' : 'mute'}`}>{fmtDate(r.secondaryDate)}</div>
        <div className=" truncate min-w-0"><strong>{r.client.legalName}</strong><span className="mute"> · {r.number ?? '—'}</span></div>
        <div className="flex justify-start">
          <Chip tone={overdue ? 'alert' : urgentSoon ? 'warn' : 'ghost'}>
            {overdue ? `+${-days}g ritardo` : days === 0 ? 'oggi' : `tra ${days}g`}
          </Chip>
        </div>
        <div className="mono text-right font-semibold">€ {fmtEur(r.total)}</div>
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {r.kind === 'invoice' ? (
            <>
              <button
                className="btn btn-sm"
                title="Segna la fattura incassata in Masamune OS, con la data di oggi: non parte nessun pagamento"
                disabled={markInvoicePaid.isPending}
                onClick={() => markInvoicePaid.mutate({ id: r.id, total: Number(r.total) })}
              >
                <Icon name="check" size={10} /> Segna incassata
              </button>
              <button
                className="btn btn-sm btn-ghost btn-icon"
                title="Invia un sollecito di pagamento al cliente"
                aria-label="Sollecita pagamento"
                disabled={sendInvoiceReminder.isPending}
                onClick={() => sendInvoiceReminder.mutate({ id: r.id, clientId: r.client.id, number: r.number })}
              >
                <Icon name="bell" size={10} />
              </button>
            </>
          ) : (
            <button
              className="btn btn-sm"
              title="Rispedisci l'email con il link firma"
              disabled={resendQuote.isPending}
              onClick={() => resendQuote.mutate({ id: r.id, clientId: r.client.id, number: r.number, title: (r.raw as QuoteItem).title })}
            >
              <Icon name="send" size={10} /> Reinvia
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      {/* Le classi `inv-sked-*` servono solo al telefono: da computer questa
          intestazione sta comoda in riga, a 390px va impilata (invoices.css). */}
      <div className="card-hd inv-sked-hd">
        <div className="flex items-center gap-3 inv-sked-main">
          <div className="icon-tile sm" style={{ ['--c' as any]: 'var(--accent)' }}><Icon name="calendar" size={14} /></div>
          <div className="inv-sked-txt">
            <div className="t-h3">Eventi attesi — 60 giorni</div>
            <div className="mute t-small">Incassi e firme in arrivo</div>
          </div>
          {/* Chip-filtro per scaglione: click = toggle (ri-click torna a tutte) */}
          <div className="flex items-center gap-1.5 inv-sked-pills" role="group" aria-label="Filtra per scadenza">
            {allGroups.map((s) => (
              <button
                key={s.key}
                className={band === s.key ? 'filter-pill is-active' : 'filter-pill'}
                aria-pressed={band === s.key}
                disabled={s.items.length === 0}
                style={s.items.length === 0 ? { opacity: 0.5 } : undefined}
                onClick={() => setBand(band === s.key ? 'all' : s.key)}
              >
                {s.label} ({s.items.length})
              </button>
            ))}
          </div>
        </div>
        <span className="mono mute text-[12px]">Atteso: € {fmtEur(total)}</span>
      </div>
      <div className="card-bd">
        {groups.map((section) => (
          <div key={section.key}>
            <div className="t-micro px-1 pt-4 pb-2 flex items-center gap-2">
              <span className={section.key === 'overdue' ? 'text-alert' : section.key === 'week' ? 'text-warn' : ''}>{section.label}</span>
              <span className="mute mono text-[12px]">{section.items.length}</span>
            </div>
            {section.items.map(renderRow)}
          </div>
        ))}
        {!upcoming.length && (
          <EmptyState
            icon="calendar"
            title="Nessun evento nei prossimi 60 giorni"
            description="Quando ci sono incassi o firme in arrivo, compaiono qui."
          />
        )}
      </div>
    </div>
  );
}
